import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { SignOut, House, FolderSimple, Bell, ChartBar, Kanban, GearSix, Calculator, Users, Receipt, Code, Shield, ShieldCheck, List, User, FileText, Plug, Tray, Bug } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        API.get("/notifications"),
        API.get("/notifications/unread-count"),
      ]);
      // Defensive: `data` MUST be an array for the .slice().map() call
      // in the notifications dropdown at line ~201 to work. Iteration
      // 67 caught a production crash where this endpoint returned a
      // non-array for at least one user, killing the entire Header
      // (which mounts on every page → looked like "dashboard flashes
      // then errors"). We now coerce to [] on any non-array response.
      setNotifications(Array.isArray(notifsRes.data) ? notifsRes.data : []);
      setUnreadCount(Number(countRes.data?.count) || 0);
    } catch {}
  }, []);

  // Inbox-triage badge. Solicitor-only endpoint; soft-fails when Outlook
  // isn't connected (server returns {count: 0, not_connected: true}).
  // We also fire a desktop browser notification when the count *rises*
  // (i.e. a new unmatched email arrived since the last poll). The browser
  // takes care of native OS toasts even when the tab is in the background.
  const lastInboxCountRef = useRef(null);
  const fetchInboxCount = useCallback(async () => {
    if (user?.role !== "solicitor") { setInboxCount(0); return; }
    try {
      const { data } = await API.get("/inbox/unmatched/count");
      const next = Number(data?.count) || 0;
      setInboxCount(next);
      const prev = lastInboxCountRef.current;
      // Only fire when we've already seen a count once (don't notify on
      // first load) and the count has actually grown.
      if (
        typeof prev === "number"
        && next > prev
        && typeof Notification !== "undefined"
        && Notification.permission === "granted"
      ) {
        const delta = next - prev;
        try {
          const n = new Notification("New email needs triage", {
            body: `${delta} new unmatched email${delta === 1 ? "" : "s"} in your Outlook — open Inbox to attach, file, or dismiss.`,
            icon: "/logo.png",
            tag: "inbox-triage",
          });
          n.onclick = () => { window.focus(); navigate("/inbox"); n.close(); };
        } catch {}
      }
      lastInboxCountRef.current = next;
    } catch {
      setInboxCount(0);
    }
  }, [user?.role, navigate]);

  useEffect(() => {
    fetchNotifications();
    fetchInboxCount();
    const interval = setInterval(() => { fetchNotifications(); fetchInboxCount(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchInboxCount]);

  const handleMarkAllRead = async () => {
    try {
      await API.put("/notifications/read-all");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      try {
        await API.put(`/notifications/${notif.id}/read`);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
      } catch {}
    }
    if (notif.case_id) navigate(`/cases/${notif.case_id}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getNotifIcon = (type) => {
    const colors = {
      stage_completed: "bg-emerald-100 text-emerald-600",
      task_assigned: "bg-blue-100 text-blue-600",
      new_note: "bg-violet-100 text-violet-600",
      document_uploaded: "bg-amber-100 text-amber-600",
      case_created: "bg-slate-100 text-slate-600",
    };
    return colors[type] || "bg-slate-100 text-slate-600";
  };

  // Solicitor navigation has been moved to tile cards on the Dashboard
  // (the user explicitly asked for the header nav to be cleared so the
  // dashboard becomes the single hub). We still keep this array for the
  // mobile hamburger menu, but the desktop inline strip is hidden.
  const navItems = [
    { label: "Dashboard", path: "/", icon: House },
    { label: "Cases", path: "/cases", icon: FolderSimple },
    ...(user?.role === "solicitor" ? [
      { label: "Pipeline", path: "/kanban", icon: Kanban },
      { label: "Inbox", path: "/inbox", icon: Tray, badge: inboxCount },
      { label: "Quotes", path: "/quotes", icon: Calculator },
      { label: "Referrers", path: "/referrers", icon: Users },
      { label: "Invoices", path: "/invoices", icon: Receipt },
      { label: "Analytics", path: "/analytics", icon: ChartBar },
    ] : []),
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-[#0a0945]" style={{ background: '#100f5d' }} data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          <div className="flex items-center gap-3 lg:gap-4 xl:gap-8 min-w-0">
            <button onClick={() => navigate("/")} className="flex items-center hover:opacity-80 transition-opacity shrink-0" data-testid="logo-btn">
              <img src="/logo.png" alt="UKC Legal Solicitors" className="h-8 sm:h-9 w-auto" />
            </button>
            {/* Header nav intentionally removed — navigation now lives as
                tiles on the Dashboard. Mobile hamburger keeps the same
                links for quick jumps from any page. */}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Hamburger menu — shown on every viewport since the
                inline nav is gone. Lets a user jump back to the
                dashboard or quickly hop to another section from any
                deep page (e.g. case detail). */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="mobile-nav-btn"
                  className="h-9 w-9 p-0 rounded-lg hover:bg-white/10">
                  <List weight="regular" className="w-5 h-5 text-slate-300" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="mobile-nav-menu">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path || (item.path === "/cases" && location.pathname.startsWith("/cases"));
                  return (
                    <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className={`cursor-pointer ${isActive ? "bg-slate-100 font-medium" : ""}`}>
                      <item.icon weight={isActive ? "fill" : "regular"} className="w-4 h-4 mr-2" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 bg-[#c87d3a] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid={`mobile-nav-${item.label.toLowerCase()}-badge`}>
                          {item.badge > 9 ? "9+" : item.badge}
                        </span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Notifications Bell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="notifications-btn" className="relative h-9 w-9 p-0 rounded-lg hover:bg-white/10">
                  <Bell weight="regular" className="w-5 h-5 text-slate-300" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] bg-[#c87d3a] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</p>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} data-testid="mark-all-read-btn" className="text-xs text-blue-600 hover:underline font-medium">Mark all read</button>
                  )}
                </div>
                <ScrollArea className="max-h-[320px]">
                  {notifications.length === 0 ? (
                    <div className="py-8 text-center">
                      <Bell weight="thin" className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">No notifications</p>
                    </div>
                  ) : (
                    notifications.slice(0, 15).map((notif) => (
                      <button key={notif.id} onClick={() => handleNotifClick(notif)} data-testid={`notif-${notif.id}`}
                        className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex gap-3 ${!notif.read ? "bg-blue-50/50" : ""}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getNotifIcon(notif.type)}`}>
                          <Bell weight="fill" className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs leading-relaxed ${!notif.read ? "text-slate-900 font-medium" : "text-slate-600"}`}>{notif.message}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                        {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />}
                      </button>
                    ))
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            <span className="hidden md:inline-flex lg:hidden xl:inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontFamily: 'Manrope, sans-serif' }}
              data-testid="user-role-badge">{user?.is_admin ? "Admin" : user?.role}</span>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu-btn" className="flex items-center gap-2 h-9 px-2 sm:px-3 rounded-lg hover:bg-white/10">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: '#c87d3a' }}>
                    <span className="text-white text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || "U"}</span>
                  </div>
                  <span className="hidden md:block lg:hidden xl:block text-sm font-medium text-white truncate max-w-[140px]" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="settings-btn" onClick={() => navigate("/settings")} className="cursor-pointer">
                  <GearSix className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="user-management-btn" onClick={() => navigate("/admin/users")} className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    User Management
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="usage-billing-btn" onClick={() => navigate("/admin/usage-billing")} className="cursor-pointer">
                    <Receipt className="w-4 h-4 mr-2" />
                    Usage &amp; Billing
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="sla-dashboard-btn" onClick={() => navigate("/admin/sla")} className="cursor-pointer">
                    <ChartBar className="w-4 h-4 mr-2" />
                    Firm SLA dashboard
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="admin-errors-btn" onClick={() => navigate("/admin/errors")} className="cursor-pointer">
                    <Bug className="w-4 h-4 mr-2" />
                    Client errors
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="pg8-audit-btn" onClick={() => navigate("/admin/pg8-audit")} className="cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Practice Guide 8 audit
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="pg67-audit-btn" onClick={() => navigate("/admin/pg67-audit")} className="cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Practice Guide 67 audit
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="email-settings-btn" onClick={() => navigate("/email-settings")} className="cursor-pointer">
                    <Receipt className="w-4 h-4 mr-2" />
                    Email & Reports
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="embed-code-btn" onClick={() => navigate("/embed-code")} className="cursor-pointer">
                    <Code className="w-4 h-4 mr-2" />
                    Embed code
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="clients-page-btn" onClick={() => navigate("/clients")} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Clients
                  </DropdownMenuItem>
                )}
                {user?.role === "solicitor" && (
                  <DropdownMenuItem data-testid="templates-page-btn" onClick={() => navigate("/templates")} className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    Templates
                  </DropdownMenuItem>
                )}
                {user?.role === "solicitor" && (
                  <DropdownMenuItem data-testid="integrations-page-btn" onClick={() => navigate("/settings/integrations")} className="cursor-pointer">
                    <Plug className="w-4 h-4 mr-2" />
                    Integrations
                  </DropdownMenuItem>
                )}
                {user?.role === "solicitor" && (
                  <DropdownMenuItem data-testid="questionnaires-settings-btn" onClick={() => navigate("/settings/questionnaires")} className="cursor-pointer">
                    <List className="w-4 h-4 mr-2" />
                    Questionnaires
                  </DropdownMenuItem>
                )}
                {user?.role === "solicitor" && (
                  <DropdownMenuItem data-testid="ta-templates-settings-btn" onClick={() => navigate("/settings/ta-templates")} className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    TA form templates
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                  <SignOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
